import { connect } from '@cerebral/react'
import { Box, CardContent, LinearProgress, Typography } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { state } from 'cerebral'
import cc from 'classcat'
import { times } from 'ramda'
import React, { useMemo } from 'react'
import { EngagementMetrics } from '../../../types/Metrics'
import FlatCard from '../../elements/FlatCard'
import CompactLayout from './elements/CompactLayout'

const getScoreBand = (score: number | null, theme: Theme) => {
  if (!score) {
    return {
      color: theme.palette.grey[600],
      label: '',
    }
  }

  if (score >= 70) {
    return {
      color: theme.palette.success.main,
      label: 'Healthy',
    }
  }

  if (score >= 50) {
    return {
      color: theme.palette.warning.main,
      label: 'Moderate',
    }
  }

  return {
    color: theme.palette.error.main,
    label: 'Low',
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    borderRadius: 0,
  },
  compactContainer: {
    cursor: 'default',
  },
  cardLocked: {
    backgroundColor: theme.palette.grey[100],
    borderWidth: 1,
    borderColor: theme.palette.grey[400],
    opacity: 0.7,
  },
  label: {
    lineHeight: 1.4,
  },
  subSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  value: {
    fontWeight: 600,
  },
  detail: {
    marginTop: theme.spacing(0.5),
  },
}))

type EngagementScoreCardExternalProps = { compact?: boolean }

interface EngagementScoreCardDeps {
  analyticsLive: { metrics: EngagementMetrics }
}

type EngagementScoreCardProps = EngagementScoreCardExternalProps & EngagementScoreCardDeps

const useScoreMetrics = (trackingRate: number, engagementScore: number, weeklyScores: number[]) => {
  return useMemo(() => {
    const isLocked: boolean = trackingRate < 80

    if (isLocked) {
      return {
        isLocked: true,
        value: `${Math.round(trackingRate)}%`,
        progressValue: Math.min((trackingRate / 80) * 100, 100),
      }
    }

    const score = engagementScore != null ? Math.round(engagementScore) : null
    if (score == null) {
      return {
        isLocked: false,
        value: '—',
        progressValue: null,
      }
    }

    return {
      isLocked: false,
      value: String(score),
      progressValue: null,
    }
  }, [trackingRate, engagementScore, weeklyScores])
}

interface ScoreLabelProps {
  label: string
  color: string
  locked: boolean
}

const ScoreLabel = ({ locked = false, label, color }: ScoreLabelProps) => {
  if (!label || locked) {
    return null
  }

  return (
    <Box sx={{ backgroundColor: alpha(color, 0.2), borderRadius: 3, px: 1, py: 0.3, display: 'flex', justifyContent: 'center' }}>
      <Typography component="span" sx={{ color, fontSize: 11, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  )
}

interface SparkLineProps {
  color: string
  scores: number[]
  locked: boolean
}

const SparkLine = ({ color, scores, locked = false }: SparkLineProps) => {
  if (!scores.length || locked) {
    return null
  }

  const values = useMemo(() => {
    const length = 4 * 28 // Every six hours for four weeks
    let lastScore = 0
    return scores.length < length
      ? times(() => 0, length - scores.length).concat(
          scores.map((v) => {
            // Fill gaps with last numeric value.
            lastScore = v ?? lastScore
            return lastScore
          })
        )
      : scores
  }, [scores])

  return (
    <Box sx={{ flexGrow: 1, marginLeft: 6 }}>
      <SparkLineChart
        data={values}
        height={30}
        width={70}
        yAxis={{ min: 0, max: 100 }}
        colors={[color]}
        plotType="line"
        slots={{ line: () => null }}
        area
        disableAxisListener
      />
    </Box>
  )
}

type ProgressBarProps = {
  locked: boolean
  progressValue: number
}

const ProgressBar = ({ locked, progressValue }: ProgressBarProps) => {
  if (!locked || progressValue === null) {
    return null
  }

  return <LinearProgress color="secondary" variant="determinate" value={progressValue!} sx={{ mt: 0.5, height: 4, borderRadius: 3 }} />
}

type DetailSectionProps = {
  locked: boolean
}

type ConnectedProps = {
  analyticsLive: {
    metrics: EngagementMetrics
  }
}

const DetailSection = connect<DetailSectionProps, ConnectedProps>(
  {
    analyticsLive: state`analyticsLive.current`,
  },
  ({ locked = false, analyticsLive }: DetailSectionProps & ConnectedProps) => {
    const classes = useStyles()
    const { metrics } = analyticsLive
    const { engagementScore, activeResidents, distinctEventCategories, trackableActivities: { tracked, total } = {} } = metrics || {}

    const score = engagementScore?.score?.current != null ? Math.round(engagementScore.score.current) : null
    const trackingRate = tracked?.count && total?.count ? (tracked?.count / total?.count) * 100 : 0

    if (locked) {
      return (
        <Box className={classes.detail}>
          <>
            <Typography variant="body2" color="textSecondary">
              Track attendance on 80% of activities to unlock this insight.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {Math.max(0, Math.ceil(80 - trackingRate))}% to go
            </Typography>{' '}
          </>
        </Box>
      )
    }

    const participationRate = Math.round(activeResidents?.participationRate?.percent || 0)
    const attendancePercent = Math.round(distinctEventCategories?.attendees?.percent || 0)

    return (
      <Typography variant="body2" color="textSecondary" className={classes.detail}>
        {score === null
          ? 'Computing engagement score...'
          : attendancePercent < 60
            ? `Participation: ${participationRate}% (×0.6) + Breadth: ${attendancePercent}% (×0.4)`
            : 'Score based on participation rate only. Categorize activities to include engagement breadth.'}
      </Typography>
    )
  }
)

const EngagementScoreCard = connect<EngagementScoreCardExternalProps, EngagementScoreCardDeps>(
  {
    analyticsLive: state`analyticsLive.current`,
  },
  ({ compact = true, analyticsLive }: EngagementScoreCardProps) => {
    const classes = useStyles()
    const theme = useTheme()
    const { metrics } = analyticsLive
    const { engagementScore, trackableActivities: { tracked, total } = {} } = metrics || {}

    const score = engagementScore?.score?.current != null ? Math.round(engagementScore.score.current) : null
    const scores = engagementScore?.history?.fourWeeks

    const trackingRate = tracked?.count && total?.count ? (tracked?.count / total?.count) * 100 : 0
    const { isLocked, value: scoreValue, progressValue } = useScoreMetrics(trackingRate, metrics?.engagementScore?.score?.current as number, scores)

    const { color, label } = getScoreBand(score, theme)
    const statusColor = isLocked ? theme.palette.grey[500] : score === null ? theme.palette.text.primary : color

    const content = (
      <Box className={cc([classes.container, compact && [classes.compactContainer, isLocked && classes.cardLocked]])}>
        <Typography variant="overline" color="textSecondary" className={classes.label}>
          Engagement Health Score
        </Typography>

        <Box className={classes.subSection}>
          <Typography variant="h5" component="span" className={classes.value} style={{ color: statusColor }}>
            {scoreValue}
          </Typography>

          <ScoreLabel label={label} color={color} locked={isLocked} />
          <SparkLine scores={scores || []} color={color} locked={isLocked} />
        </Box>

        <ProgressBar locked={isLocked} progressValue={progressValue as number} />
        {!compact ? <DetailSection locked={isLocked} /> : null}
      </Box>
    )

    if (compact) {
      return (
        <CompactLayout label={label} compact={compact} value={scoreValue} content={content} locked={isLocked}>
          <DetailSection locked={isLocked} />
        </CompactLayout>
      )
    }

    return (
      <FlatCard>
        <CardContent className={cc([isLocked && classes.cardLocked])}>{content}</CardContent>
      </FlatCard>
    )
  }
)

export default EngagementScoreCard
